import {
  type ApproverDecision,
  type DecisionBatch,
  slotOf,
  statementProseOrigin,
} from '@zevik/schemas';
import type { ClarificationsResponse, FindingEntry } from '../api/client';
import { CONDITIONAL_PREFIX, reportedBand, SEVERITY_LIMIT } from '../review/vocabulary';
import { Band } from './Band';
import { Grounding } from './Grounding';
import { DecisionControl } from './DecisionControl';
import type { DecisionDraft } from '../review/decision';

/**
 * One finding, in full (docs/DECISIONS.md ZD-112).
 *
 * ## No fact without its provenance available
 *
 * The prose is the pipeline's own transcript — `title`, `description` and
 * `rationale` are rendered from the model's witnesses at store time — and the
 * grounding block below it carries each witness with its basis and its citations.
 * Neither is shown without the other: a finding shown without its grounding is a
 * different object wearing the same name.
 *
 * `prose_origin` is stated per finding rather than as a page-level assurance. The
 * report's provenance sentence is a claim about a *document* and is not carried
 * here; what a reviewer needs is whether the sentences in front of *them* were
 * written by a model, which `statementProseOrigin` computes from the statement
 * itself rather than asserting.
 *
 * ## A rewrite is shown beside the pipeline's sentence, never instead of it
 *
 * `GET /findings` lays the standing decision's rewrite on the statement as
 * `authored_text` (ZD-125 §1), and this shows it as the finding's description
 * or rationale with the pipeline's own wording beneath — so *what did the
 * pipeline say* is on the screen with *what the reviewer wrote*, which is what
 * a slot carrying both is for (ZD-061 §2). `finding.description` and
 * `finding.rationale` are the transcript's rendering and stay what they were.
 */

const ORIGIN_WORDS = {
  rendered: 'Every sentence here is generated from the model built out of the documents.',
  authored: 'Some sentences here were written by a language model working from that model.',
  approver_edited: 'Some sentences here were written by the reviewer who approved them.',
} as const;

interface Props {
  finding: FindingEntry;
  standing: ApproverDecision | undefined;
  /** The group the standing decision was made in, where it was made in one (ZD-127 §3). */
  batch: DecisionBatch | undefined;
  clarifications: ClarificationsResponse;
  onRecord: (draft: DecisionDraft) => Promise<void>;
  recordingUnavailable: string | null;
  /** Bumped when a session is restored, so a refusal about the old one is cleared. */
  sessionRestored: number;
}

export function FindingDetail({
  finding,
  standing,
  batch,
  clarifications,
  onRecord,
  recordingUnavailable,
  sessionRestored,
}: Props): React.JSX.Element {
  const band = reportedBand(finding.finding);
  const conditional = finding.finding.conditional;
  const origin = statementProseOrigin(finding.statement);
  const rewritten = {
    description: slotOf(finding.statement, 'threat_action').authored_text,
    rationale: slotOf(finding.statement, 'grounds').authored_text,
  };

  /*
   * What this deferral is waiting on, read from the server's own ledger. The join
   * keys a decision's structural key against a clarification's
   * `(threat_pattern_id, subject_id)` and is derived in the orchestrator on
   * ZD-017's argument; a browser redoing it would be the second parser of a
   * format one module writes.
   */
  const waiting = clarifications.ledger.findings.find(
    (entry) => entry.finding_structural_key === finding.finding_structural_key,
  );

  return (
    <div className="detail__inner finding">
      {conditional.is_conditional ? (
        <p className="finding__standing marker">{CONDITIONAL_PREFIX}</p>
      ) : null}
      <h2>{finding.finding.title}</h2>

      <div className="finding__strap">
        <Band band={band} />
        <span>{finding.finding.analysis_lens}</span>
        <span>
          {finding.finding.pack_id}@{finding.finding.pack_version}
        </span>
        <span>{finding.finding.threat_pattern_id}</span>
      </div>

      <div className="prose">
        {rewritten.description === null ? (
          <p>{finding.finding.description}</p>
        ) : (
          <>
            <p className="rewritten">{rewritten.description}</p>
            <p className="transcript">
              <span className="transcript__label">The pipeline’s own wording: </span>
              {finding.finding.description}
            </p>
          </>
        )}
        {conditional.is_conditional && conditional.condition !== null ? (
          <p>
            <em>
              This is recorded conditionally: it holds if {conditional.condition}. Your answer
              decides it.
            </em>
          </p>
        ) : null}
        {rewritten.rationale === null ? (
          <p>{finding.finding.rationale}</p>
        ) : (
          <>
            <p className="rewritten">{rewritten.rationale}</p>
            <p className="transcript">
              <span className="transcript__label">The pipeline’s own wording: </span>
              {finding.finding.rationale}
            </p>
          </>
        )}
      </div>

      <Grounding statement={finding.statement} />

      {waiting === undefined ? null : (
        <section className="block">
          <h3>This deferral is waiting on</h3>
          {waiting.outstanding.length === 0 ? (
            <p className="absent">
              Every question it was waiting on has an answer recorded. It is still deferred until
              you record a new decision.
            </p>
          ) : (
            <ul className="idlist">
              {waiting.outstanding.map((ask) => (
                <li key={`${ask.object_id}|${ask.attribute}`}>
                  {ask.attribute} of {ask.object_id}
                </li>
              ))}
            </ul>
          )}
        </section>
      )}

      <DecisionControl
        finding={finding}
        standing={standing}
        batch={batch}
        onRecord={onRecord}
        unavailable={recordingUnavailable}
        sessionRestored={sessionRestored}
      />

      <p className="limit">{ORIGIN_WORDS[origin]}</p>
      <p className="limit">{SEVERITY_LIMIT}</p>
    </div>
  );
}
